import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Screen } from '../../shell/Screen';
import { RequireProfile } from '../../shell/RequireProfile';
import { Icon, type IconName } from '../../components/Icon';
import { LocaleSwitcher } from '../../components/LocaleSwitcher';
import { BottomSheet } from '../../components/BottomSheet';
import { canUseModule } from '@blauturquesa/shared/roles-lite';
import { useAuth } from '../auth/AuthProvider';
import { reloadApp, signOutCompletely } from '../auth/actions';

/** Pestanya «Més» dels perfils de gestió (§10.4). El que el perfil no pot fer, esmorteït (§10.1). */
export function MorePage() {
  const { t } = useTranslation('shell');
  const state = useAuth();
  const isSuper = state.status === 'signedIn' && state.activeRole === 'superadmin';
  const hasOpportunities = state.status === 'signedIn' && canUseModule(state.activeRole, state.modules, 'opportunities');

  const item = (to: string, icon: IconName, label: string, enabled = true, hint?: string) =>
    enabled ? (
      <li key={to}>
        <Link className="menu-row" to={to}>
          <Icon name={icon} size={22} /> <span className="grow">{label}</span> <Icon name="chevronRight" size={18} />
        </Link>
      </li>
    ) : (
      <li key={to}>
        <span className="menu-row is-disabled" aria-disabled="true" title={hint}>
          <Icon name={icon} size={22} /> <span className="grow">{label}<span className="small block">{hint}</span></span>
        </span>
      </li>
    );

  return (
    <Screen title={t('tabs.more')}>
      <RequireProfile profiles={['agent', 'superadmin']}>
        <p className="menu-section">{t('more.tools')}</p>
        <ul className="menu glass-card">
          {item('/oportunitats', 'compass', t('more.opportunities'), hasOpportunities, t('more.noModule'))}
        </ul>
        <p className="menu-section">{t('more.admin')}</p>
        <ul className="menu glass-card">
          {item('/mes/usuaris', 'users', t('more.users'), isSuper, t('more.superadminOnly'))}
          {item('/mes/skins', 'image', t('more.skins'), isSuper, t('more.superadminOnly'))}
          {item('/mes/configuracio', 'settings', t('more.settings'), false, isSuper ? t('more.comingSoon') : t('more.superadminOnly'))}
          <PublicPagesRow enabled={isSuper} />
        </ul>
        <p className="menu-section">{t('more.you')}</p>
        <ul className="menu glass-card">
          {item('/compte', 'user', t('sheet.account'))}
          <li>
            <div className="menu-row"><Icon name="globe" size={22} /> <span className="grow">{t('sheet.language')}</span> <LocaleSwitcher /></div>
          </li>
          <li>
            <button type="button" className="menu-row" onClick={reloadApp}>
              <Icon name="swap" size={22} /> <span className="grow">{t('sheet.reload')}<span className="small block muted">{t('sheet.reloadHint')}</span></span>
            </button>
          </li>
          <li>
            <button type="button" className="menu-row" onClick={() => void signOutCompletely()}>
              <Icon name="logout" size={22} /> <span className="grow">{t('sheet.signOut')}</span>
            </button>
          </li>
        </ul>
      </RequireProfile>
    </Screen>
  );
}

interface RebuildResult { published: number; missing: string[]; applied: boolean }

/**
 * Regenerar les pàgines públiques (superadmin): primer en sec, diu quantes n'hi ha i quantes falten;
 * després, amb confirmació, les torna a fer totes (§20.6). Les Functions es carreguen només en tocar-ho.
 */
function PublicPagesRow({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation('shell');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<{ kind: 'idle' | 'running' | 'error' } | { kind: 'checked' | 'done'; result: RebuildResult }>({ kind: 'idle' });

  async function run(apply: boolean) {
    setState({ kind: 'running' });
    try {
      const { call } = await import('../../firebase/functions');
      const result = await call<{ apply: boolean }, RebuildResult>('rebuildPublicViews', { apply });
      setState({ kind: apply ? 'done' : 'checked', result });
    } catch {
      setState({ kind: 'error' });
    }
  }

  if (!enabled) {
    return (
      <li>
        <span className="menu-row is-disabled" aria-disabled="true">
          <Icon name="globe" size={22} /> <span className="grow">{t('more.rebuild')}<span className="small block">{t('more.superadminOnly')}</span></span>
        </span>
      </li>
    );
  }
  return (
    <li>
      <button type="button" className="menu-row" onClick={() => { setOpen(true); void run(false); }}>
        <Icon name="globe" size={22} /> <span className="grow">{t('more.rebuild')}<span className="small block muted">{t('more.rebuildHint')}</span></span>
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} label={t('more.rebuild')}>
        <p className="sheet-section">{t('more.rebuild')}</p>
        {state.kind === 'running' && <p className="muted">{t('more.rebuildRunning')}</p>}
        {state.kind === 'error' && <p className="error">{t('more.rebuildError')}</p>}
        {state.kind === 'checked' && <p>{t('more.rebuildCheck', { published: state.result.published, missing: state.result.missing.length })}</p>}
        {state.kind === 'done' && <p>{t('more.rebuildDone', { count: state.result.published })}</p>}
        <div className="sheet-actions">
          <button type="button" className="btn grow" onClick={() => setOpen(false)}>{t('more.close')}</button>
          <button type="button" className="btn-shiny grow" disabled={state.kind !== 'checked' || state.result.published === 0} onClick={() => void run(true)}>{t('more.rebuildApply')}</button>
        </div>
      </BottomSheet>
    </li>
  );
}
